import React, { useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";
import SearchComponent from "../../../components/dashboard-components/SearchComponent";
import { useUserStore } from "../../../store/useUser";
import UsersModal from "../../../components/dashboard-components/users/UsersModal";
import { useBusinessCustomer } from "../../../sevices/hooks/account";
import CustomerTable, { ActionType } from "../../../components/customerTable";

// Define proper types for business user data
interface BusinessUserData {
  userId: string;
  businessName: string;
  contactPerson: string;
  email: string;
  phoneNumber: string;
  refereeCode: string;
  profileStatus: string;
  kycstatus: string;
  invitedBy?: string;
  transactionCount: number;
  createdAt: string;
  [key: string]: any; // For any additional properties
}

interface PaginatedResponse {
  data: BusinessUserData[];
  totalPages: number;
  currentPage: number;
}

const Businesses: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>(
    searchParams.get("search") || ""
  );

  // User store for user details modal
  const setUserDetailsModal = useUserStore((state) => state.setShowUserDetails);
  const showUserDetailsModal = useUserStore((state) => state.showUserDetails);
  const setUserId = useUserStore((state) => state.setSelectedUsersId);

  // Fetch business users data
  const {
    data: businessUser,
    isLoading,
    refetch,
  } = useBusinessCustomer({
    pageSize,
    page,
    search: searchTerm,
  });

  // Create a memoized refetch function with useCallback
  const handleRefetch = useCallback(async () => {
    try {
      return await refetch();
    } catch (error) {
      console.error("Failed to refetch business user data:", error);
      return null;
    }
  }, [refetch]);

  // Debounced search to prevent excessive API calls
  const debouncedSearch = useCallback(
    _.debounce((term: string) => {
      setSearchTerm(term);
      setPage(1); // Reset to first page when searching
    }, 500),
    []
  );

  const handleSearch = useCallback((value: string) => {
    debouncedSearch(value);
  }, [debouncedSearch]);

  // Handle action from CustomerTable
  const handleTableAction = useCallback(
    async (action: ActionType, user: any) => {
      console.log(`Action ${action.toLowerCase()} performed on business:`, user.userId);

      // Different actions might need different handling
      if (
        action === ActionType.Activate ||
        action === ActionType.Suspend ||
        action === ActionType.Delete ||
        action === ActionType.Block
      ) {
        // The CustomerTable component will handle the refetch
      }
    },
    []
  );

  // Format data for table consumption
  const tableData = useMemo(() => {
    if (!businessUser?.data) return [];
    return businessUser.data;
  }, [businessUser]);

  // Calculate total pages properly
  const totalPages = useMemo(() => {
    return businessUser?.data?.totalPages || 1;
  }, [businessUser?.data?.totalPages]);

  return (
    <>
      <div className="h-100vh w-full bg-white p-5 md:pr-5 md:pl-8">
        <div className="flex flex-row items-center justify-between mt-5">
          <h1 className="font-bold text-2xl text-[#071827] md:pb-2">
            Business Users
          </h1>
        </div>

        <SearchComponent
          placeholder="Search business"
          onSearch={handleSearch}
        />

        <div>
          {/* <CustomerTable
            refetchTable={handleRefetch}
            data={tableData || []}
            isLoading={isLoading}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            searchTerm={searchTerm}
            isBusiness={true}
            onAction={handleTableAction}
          /> */}
          <CustomerTable
            refetchTable={handleRefetch}
            data={businessUser?.data?.data || []} 
            isLoading={isLoading}
            page={page}
            totalPages={businessUser?.data?.totalPages || 1}
            onPageChange={setPage}
            searchTerm={searchTerm}
            isBusiness={true}
            onAction={handleTableAction}
          />
        </div>
      </div>

      {/* User details modal */}
      {showUserDetailsModal && (
        <UsersModal
          isOpen={showUserDetailsModal}
          onClose={() => setUserDetailsModal(false)}
          refetchTable={handleRefetch}
        />
      )}
    </>
  );
};

export default Businesses;